import { useMemo, type ComponentProps } from "react"
import { formatNumber } from "@/v2-common/utils/utils.formatting"
import cn from "@/v2-ui/utils/utils.cn"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import Badge from "@/v2-backoffice-client/components/badge"
import Link from "@/v2-backoffice-client/components/link"
import RelativeDate from "@/v2-backoffice-client/components/RelativeDate"
import LicenseGoverningBadge from "@/v2-backoffice-client/license/LicenseGoverningBadge"
import { useLicenseStatusesQuery } from "@/v2-backoffice-client/license/license.statuses.query"
import PortfolioSectionHeader from "@/v2-backoffice-client/portfolio/PortfolioSectionHeader"
import UsageTrendBadge from "@/v2-backoffice-client/usage/UsageTrendBadge"
import {
  formatAppCode,
  formatTrendDetail,
  getAppCodeColor,
} from "@/v2-backoffice-client/usage/usage.constants"
import UsageReportKpi from "@/v2-backoffice-client/usage/report/UsageReportKpi"
import UsageReportCard from "@/v2-backoffice-client/usage/report/UsageReportCard"
import { mcpTrendPct } from "@/v2-backoffice-client/usage/mcp/usage.mcp.utils"
import { useUsageLicenseDeniedPortfolioQuery } from "@/v2-backoffice-client/usage/denied/usage.deniedPortfolio.query"
import {
  LICENSE_DENIED_PORTFOLIO_LIST_LIMIT,
  LICENSE_DENIED_SECTION_TOOLTIP,
} from "@/v2-backoffice-client/usage/denied/usage.denied.constants"
import {
  deriveLicenseDeniedWindow,
  formatLicenseDeniedFeature,
  type UsageLicenseDeniedFeatureRow,
} from "@/v2-backoffice-client/usage/denied/usage.denied.utils"
import UsageLicenseDeniedFeatureList from "@/v2-backoffice-client/usage/denied/UsageLicenseDeniedFeatureList"
import type { UsageLicenseDeniedPortfolioQuery } from "@/v2-backoffice-client/usage/denied/__types__/usage.deniedPortfolio.query"
import { buildCustomerActivityLink } from "@/v2-backoffice-client/portfolio/portfolio.utils"
import { usageKpiGridClass } from "@/v2-backoffice-client/usage/report/usage.report.utils"

export type PortfolioLicenseDemandSectionProps = ComponentProps<"section"> & {
  /** Resolved server-side scope (facets resolved to ids); null = everyone. */
  customerIds: string[] | null
  excludeCustomerIds: string[]
  excludeUserIds: number[]
}

type PortfolioLicenseDemandCustomer =
  UsageLicenseDeniedPortfolioQuery["getUsageLicenseDeniedForPortfolio"]["Customers"][number]

const EMPTY_FEATURES: UsageLicenseDeniedFeatureRow[] = []
const EMPTY_CUSTOMERS: PortfolioLicenseDemandCustomer[] = []

// The upsell pipeline, fleet-wide: which locked features people keep
// asking for (the demand ranking that tells sales and product what to
// package or sell) and which customers are asking, with the license they
// hold today. The counterpart of the idle-licenses section next to it:
// that one is paying without using, this one is using up to the lock
// without paying. Placepoint's own clicks on simulated locks are left out.
function PortfolioLicenseDemandSection(
  props: PortfolioLicenseDemandSectionProps,
) {
  const {
    customerIds,
    excludeCustomerIds,
    excludeUserIds,
    className,
    ...sectionProps
  } = props

  const { data, loading, error } = useUsageLicenseDeniedPortfolioQuery({
    variables: {
      customerIds,
      excludeCustomerIds:
        excludeCustomerIds.length > 0 ? excludeCustomerIds : null,
      excludeUserIds: excludeUserIds.length > 0 ? excludeUserIds : null,
    },
  })
  const { data: licenseStatusData } = useLicenseStatusesQuery()
  const licenseStatusNamesByCode = useMemo(
    () =>
      new Map(
        (licenseStatusData?.getLicenseStatuses ?? []).map((status) => [
          status.Code,
          status.Name,
        ]),
      ),
    [licenseStatusData?.getLicenseStatuses],
  )

  const demand = data?.getUsageLicenseDeniedForPortfolio
  const features = demand?.Features ?? EMPTY_FEATURES
  const customers = demand?.Customers ?? EMPTY_CUSTOMERS
  const summary = useMemo(() => deriveLicenseDeniedWindow(features), [features])
  const asking = useMemo(
    () => customers.filter((customer) => customer.Attempts28d > 0),
    [customers],
  )
  const people28d = asking.reduce((sum, customer) => sum + customer.Users28d, 0)
  const visibleCustomers = asking.slice(0, LICENSE_DENIED_PORTFOLIO_LIST_LIMIT)

  return (
    <section className={cn("flex flex-col gap-5", className)} {...sectionProps}>
      <PortfolioSectionHeader
        eyebrow="Demand"
        title="Locked features in demand"
        lead="People who tried to use a feature their license does not include — a locked button in Fusion, an MCP sign-in without a seat. What they want and do not have: the upsell pipeline."
        info={LICENSE_DENIED_SECTION_TOOLTIP}
      >
        {summary.apps.map((app) => (
          <Badge key={app} variant="outline" size="xs">
            <span
              className="mr-1 inline-block size-2 rounded-full"
              style={{ backgroundColor: getAppCodeColor(app) }}
            />
            {formatAppCode(app)}
          </Badge>
        ))}
        <span className="ml-auto text-xs text-muted-foreground">
          Refused attempts in the last 28 days · Placepoint staff excluded
        </span>
      </PortfolioSectionHeader>

      <ErrorMessage error={error} />

      {loading && !demand ? (
        <p className="text-xs text-muted-foreground">Loading…</p>
      ) : summary.attempts28d === 0 ? (
        <p className="rounded-md border border-dashed px-3 py-6 text-center text-xs text-muted-foreground">
          Nobody in scope has met a license lock in the last 28 days. Rows
          appear the morning after someone clicks a locked feature or is refused
          by an MCP server.
        </p>
      ) : (
        <>
          <div className={usageKpiGridClass(3)}>
            <UsageReportKpi
              label="Refused attempts"
              tone="warning"
              value={
                <>
                  {formatNumber(summary.attempts28d)}
                  <UsageTrendBadge
                    trendPct={summary.trendPct}
                    detail={formatTrendDetail(
                      summary.attempts28d,
                      summary.attemptsPrev28d,
                    )}
                  />
                </>
              }
              sub="in the last 28 days, against the four weeks before"
            />
            <UsageReportKpi
              label="Customers asking"
              value={asking.length}
              sub={`${formatNumber(people28d)} ${people28d === 1 ? "person" : "people"} in the last 28 days`}
            />
            <UsageReportKpi
              label="Features asked for"
              value={summary.features28d}
              sub={
                summary.lastAt ? (
                  <>
                    last refusal <RelativeDate date={summary.lastAt} />
                  </>
                ) : (
                  "in the last 28 days"
                )
              }
            />
          </div>

          <div className="usage-report-grid usage-report-grid--two">
            <UsageReportCard
              title="Features in demand"
              caption="Refused attempts in the last 28 days, with the people and customers behind each."
              tone="warning"
            >
              <UsageLicenseDeniedFeatureList
                features={features}
                limit={LICENSE_DENIED_PORTFOLIO_LIST_LIMIT}
                showPeople
                showCustomers
                emptyText="No refused attempt in the last 28 days."
              />
            </UsageReportCard>

            <UsageReportCard
              title="Customers asking"
              caption="Most attempts in the last 28 days first, with the license each holds today."
            >
              <div className="overflow-x-auto">
                <table className="usage-report-table">
                  <thead>
                    <tr>
                      <th>Customer</th>
                      <th>License</th>
                      <th className="num">Attempts</th>
                      <th className="num">People</th>
                      <th>Tried most</th>
                      <th>Last</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleCustomers.map((customer) => (
                      <tr key={customer.ExternalCustomerId}>
                        <td>
                          <Link
                            to={buildCustomerActivityLink(
                              customer.ExternalCustomerId,
                            )}
                            iconSrc={null}
                            className="block max-w-48 truncate font-medium"
                            title={customer.LicensePlanNames ?? undefined}
                          >
                            {customer.Name}
                          </Link>
                        </td>
                        <td className="whitespace-nowrap">
                          <LicenseGoverningBadge
                            size="xs"
                            license={{
                              LicenseStatusCode: customer.LicenseStatusCode,
                              LicenseTrialEndsAt: customer.LicenseTrialEndsAt,
                              LicensePlanNames: customer.LicensePlanNames,
                              LicenseRenewsAt: customer.LicenseRenewsAt,
                              LicenseEndedAt: customer.LicenseEndedAt,
                            }}
                            statusName={licenseStatusNamesByCode.get(
                              customer.LicenseStatusCode,
                            )}
                          />
                        </td>
                        <td className="num">
                          <span className="inline-flex items-center justify-end gap-1.5">
                            {formatNumber(customer.Attempts28d)}
                            <UsageTrendBadge
                              trendPct={mcpTrendPct(
                                customer.Attempts28d,
                                customer.AttemptsPrev28d,
                              )}
                              detail={formatTrendDetail(
                                customer.Attempts28d,
                                customer.AttemptsPrev28d,
                              )}
                            />
                          </span>
                        </td>
                        <td className="num">{customer.Users28d}</td>
                        <td
                          className="max-w-56 truncate"
                          title={customer.TopFeatures.map(
                            (feature) =>
                              `${formatLicenseDeniedFeature(feature.FeatureCode, feature.FeatureName)} (${formatNumber(feature.Attempts)})`,
                          ).join(", ")}
                        >
                          {customer.TopFeatures[0]
                            ? `${formatLicenseDeniedFeature(customer.TopFeatures[0].FeatureCode, customer.TopFeatures[0].FeatureName)} (${formatNumber(customer.TopFeatures[0].Attempts)})`
                            : "—"}
                          {customer.Features28d > 1 && (
                            <span className="text-muted-foreground">
                              {" "}
                              +{customer.Features28d - 1} more
                            </span>
                          )}
                        </td>
                        <td className="whitespace-nowrap text-muted-foreground">
                          {customer.LastAt ? (
                            <RelativeDate date={customer.LastAt} />
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {asking.length > visibleCustomers.length && (
                <p className="text-[11px] text-muted-foreground">
                  + {asking.length - visibleCustomers.length} more — open a
                  customer's Activity tab for its full list
                </p>
              )}
            </UsageReportCard>
          </div>
        </>
      )}
    </section>
  )
}

export default PortfolioLicenseDemandSection
